import { useId, useRef, type ComponentProps, type ReactNode } from 'react'
import {
  AudioLines,
  Captions,
  Expand,
  Minimize,
  Pause,
  Play,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  ChevronUp,
} from 'lucide-react'
import { Calendar } from './Calendar'
import { MobilePicker } from './MobilePicker'
import { monthOf } from './time'
import { Timeline } from './Timeline'
import type { useArchivePlayer } from './useArchivePlayer'

export interface NavigationAction {
  disabled: boolean
  onClick: () => void
}

interface PlayerFooterProps {
  expanded: boolean
  onToggleExpanded: () => void
  children?: ReactNode
  timeline?: ComponentProps<typeof Timeline>
  calendar?: ComponentProps<typeof Calendar>
  calendarOpen?: boolean
  onCalendarOpenChange?: (open: boolean) => void
  mobilePickers?: boolean
  player: Pick<
    ReturnType<typeof useArchivePlayer>,
    | 'playing'
    | 'recording'
    | 'hasAudio'
    | 'toggle'
    | 'muted'
    | 'setMuted'
    | 'rate'
    | 'setRate'
  >
  navigateSpeech: boolean
  onToggleSpeech: () => void
  captions: boolean
  onToggleCaptions: () => void
  previous: NavigationAction
  next: NavigationAction
  fullscreen: boolean
  onFullscreen: () => void
}

export function PlayerFooter({
  expanded,
  onToggleExpanded,
  children,
  timeline,
  calendar,
  calendarOpen = false,
  onCalendarOpenChange,
  mobilePickers = false,
  player,
  navigateSpeech,
  onToggleSpeech,
  captions,
  onToggleCaptions,
  previous,
  next,
  fullscreen,
  onFullscreen,
}: PlayerFooterProps) {
  const toolsId = useId()
  const compactCalendarTrigger = useRef<HTMLButtonElement>(null)
  const hasSpeech = (timeline?.speech?.length ?? 0) > 0
  const showDate = !expanded || !children
  const compactCalendar = !expanded && Boolean(calendar?.selected && onCalendarOpenChange)
  const stamp = timeline
    ? (() => {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: timeline.data.timezone,
          ...(showDate
            ? ({ year: 'numeric', month: 'short', day: 'numeric' } as const)
            : {}),
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
        }).formatToParts(timeline.clock)
        const yearIndex = showDate ? parts.findIndex((part) => part.type === 'year') : -1
        if (yearIndex === -1) return parts.map((part) => part.value)
        const dateText = parts
          .slice(0, yearIndex + 1)
          .map((part) => part.value)
          .join('')
        const timeStart =
          parts[yearIndex + 1]?.type === 'literal' ? yearIndex + 2 : yearIndex + 1
        const timeText = parts
          .slice(timeStart)
          .map((part) => part.value)
          .join('')
        return (
          <>
            {compactCalendar && calendar ? (
              <span className="calendar-anchor">
                <button
                  type="button"
                  className={`timestamp-date-trigger ${calendarOpen ? 'selected' : ''}`}
                  ref={compactCalendarTrigger}
                  aria-label="Open calendar"
                  aria-expanded={calendarOpen}
                  onClick={() => {
                    calendar.onMonth(monthOf(calendar.selected))
                    onCalendarOpenChange!(!calendarOpen)
                  }}
                >
                  {dateText}
                </button>
                {calendarOpen && (
                  <MobilePicker
                    mobile={mobilePickers}
                    label="Choose recording date"
                    trigger={compactCalendarTrigger}
                    onClose={calendar.onClose}
                  >
                    <Calendar {...calendar} modal={mobilePickers} />
                  </MobilePicker>
                )}
              </span>
            ) : (
              dateText
            )}
            <span className="timestamp-separator"> · </span>
            {timeText}
          </>
        )
      })()
    : 'Select a recording'
  return (
    <div className={`bottom-dock hud ${expanded ? 'dock-expanded' : 'dock-compact'}`}>
      <div id={toolsId} hidden={!expanded}>
        {children}
      </div>
      {timeline && (
        <Timeline
          {...timeline}
          compact={!expanded}
          timestamp={
            <time
              className="dock-timestamp"
              dateTime={new Date(timeline.clock).toISOString()}
            >
              {stamp}
            </time>
          }
        />
      )}
      <div className="transport">
        <div className="playback-controls">
          <button
            className="play-button"
            aria-label={player.playing ? 'Pause' : 'Play'}
            disabled={!player.recording}
            onClick={player.toggle}
          >
            {player.playing ? (
              <Pause size={21} fill="currentColor" />
            ) : (
              <Play size={21} fill="currentColor" />
            )}
          </button>
          <div className="audio-controls">
            <span className="divider" />
            <button
              aria-label={player.muted ? 'Unmute' : 'Mute'}
              disabled={!player.recording || !player.hasAudio}
              onClick={() => player.setMuted(!player.muted)}
            >
              {player.muted ? <VolumeX size={19} /> : <Volume2 size={19} />}
            </button>
            <select
              aria-label="Playback speed"
              value={player.rate}
              onChange={(e) => player.setRate(Number(e.target.value))}
            >
              {[0.5, 1, 2, 4, 8].map((rate) => (
                <option value={rate} key={rate}>
                  {rate}×
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="seek-controls">
          <button
            aria-label={
              navigateSpeech
                ? 'Restart or previous speech region'
                : 'Restart or previous clip'
            }
            title={
              navigateSpeech
                ? 'Restart or previous speech region'
                : 'Restart or previous clip'
            }
            {...previous}
          >
            <SkipBack size={18} />
          </button>
          <button
            aria-label={navigateSpeech ? 'Next speech region' : 'Next recording'}
            title={navigateSpeech ? 'Next speech region' : 'Next clip'}
            {...next}
          >
            <SkipForward size={18} />
          </button>
        </div>
        <div className="view-controls">
          {hasSpeech && (
            <>
              <button
                className="captions-toggle"
                aria-label="Captions"
                aria-pressed={captions}
                onClick={onToggleCaptions}
                title="Toggle captions"
              >
                <Captions size={18} />
              </button>
              <button
                className="speech-navigation"
                aria-label="Navigate speech"
                aria-pressed={navigateSpeech}
                title="Navigate speech regions instead of clips"
                onClick={onToggleSpeech}
              >
                <AudioLines size={18} />
              </button>
            </>
          )}
          <button
            aria-label="Toggle fullscreen"
            aria-pressed={fullscreen}
            title={fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            onClick={onFullscreen}
          >
            {fullscreen ? <Minimize size={19} /> : <Expand size={19} />}
          </button>
          <button
            className="disclosure-toggle"
            aria-label={expanded ? 'Hide archive controls' : 'Show archive controls'}
            aria-expanded={expanded}
            aria-controls={toolsId}
            onClick={onToggleExpanded}
            title={expanded ? 'Hide archive controls' : 'Show archive controls'}
          >
            <ChevronUp size={20} />
          </button>
        </div>
      </div>
    </div>
  )
}
